import React, { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTableContext } from '../../context/TableContext';

/**
 * Priority options with their colors
 */
const PRIORITY_OPTIONS = [
  { id: 'high', name: 'High', color: '#ef4444' },
  { id: 'medium', name: 'Medium', color: '#f59e0b' },
  { id: 'low', name: 'Low', color: '#22c55e' },
  { id: null, name: 'None', color: '#6b7280' },
];

/**
 * Get priority config by ID
 */
const getPriorityConfig = (priorityId) => {
  return PRIORITY_OPTIONS.find(p => p.id === priorityId) || PRIORITY_OPTIONS[3]; // Default to 'None'
};

/**
 * Priority cell with dropdown for priority selection
 * Memoized to prevent re-renders when other cells change
 */
const PriorityCell = memo(({ row }) => {
  const { onPriorityChange } = useTableContext();
  const task = row.original;

  // Get priority config - memoized
  const priorityConfig = useMemo(() => getPriorityConfig(task.priority), [task.priority]);

  // Dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Unique ID for this dropdown instance
  const dropdownId = useRef(`priority-${task.id}`).current;

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;
    
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Close when another dropdown opens (only listen when this menu is open)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleCloseDropdowns = (e) => {
      if (e.detail?.exceptId !== dropdownId) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('closeTableDropdowns', handleCloseDropdowns);
    return () => window.removeEventListener('closeTableDropdowns', handleCloseDropdowns);
  }, [isOpen, dropdownId]);

  const handleButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      // Close other dropdowns before opening this one
      window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: dropdownId } }));
      
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(prev => !prev);
  }, [isOpen, dropdownId]);

  const handlePrioritySelect = useCallback((priorityId) => {
    onPriorityChange?.(task.id, priorityId);
    setIsOpen(false);
  }, [onPriorityChange, task.id]);

  // Memoize button styles
  const buttonStyle = useMemo(() => ({
    background: `${priorityConfig.color}15`,
    color: priorityConfig.color,
    borderColor: `${priorityConfig.color}30`,
  }), [priorityConfig.color]);

  return (
    <>
      <button
        ref={buttonRef}
        className="table-view__priority-btn"
        style={buttonStyle}
        onClick={handleButtonClick}
      >
        <span 
          className="table-view__priority-dot" 
          style={{ background: priorityConfig.color }} 
        />
        {priorityConfig.name}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="table-view__dropdown table-view__dropdown--priority"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {PRIORITY_OPTIONS.map((option) => (
              <div
                key={option.id ?? 'none'}
                className={`table-view__dropdown-item table-view__dropdown-item--priority ${
                  task.priority === option.id ? 'table-view__dropdown-item--active' : ''
                }`}
                onClick={() => handlePrioritySelect(option.id)}
              >
                <span className="table-view__priority-dot" style={{ background: option.color }} />
                <span className="table-view__priority-name">{option.name}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}, (prevProps, nextProps) => {
  const prevTask = prevProps.row.original;
  const nextTask = nextProps.row.original;
  return prevTask.id === nextTask.id && prevTask.priority === nextTask.priority;
});

export default PriorityCell;
